import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useI18n } from "@/context/I18nContext";
import { pagePath } from "@/i18n/pages";
import { dirPath, dl } from "@/i18n/directory";
import { eggcalcPath, eggcalcData } from "@/i18n/eggcalc";
import { henprofitPath, henprofitData } from "@/i18n/henprofit";
import { hatchPath, hatchData } from "@/i18n/hatch";
import { feedPath, feedData } from "@/i18n/feed";
import { coopPath, coopData } from "@/i18n/coop";
import { forumPath, fg as forumT } from "@/i18n/forum";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { FarmIcon } from "@/components/FarmIcon"; // eslint-disable-line no-unused-vars

const DEMO_LABEL = { fr: "Démo", bg: "Демо", el: "Ντέμο" };
const TOOLS_LABEL = { fr: "Outils", en: "Tools", es: "Herramientas", it: "Strumenti", de: "Werkzeuge", nl: "Hulpmiddelen", sv: "Verktyg", da: "Værktøjer", bg: "Инструменти", el: "Εργαλεία", hr: "Alati", et: "Tööriistad", fi: "Työkalut", hu: "Eszközök", lv: "Rīki", lt: "Įrankiai", pl: "Narzędzia", pt: "Ferramentas", ro: "Instrumente", sk: "Nástroje", sl: "Orodja", cs: "Nástroje" };

const PREFETCH = {
  pricing: () => import("@/pages/MarketingPage"),
  blog: () => import("@/pages/Blog"),
  forum: () => import("@/pages/Forum"),
  directory: () => import("@/pages/Annuaire"),
  demo: () => import("@/pages/DemoPage"),
  tools: () => import("@/pages/EggCostCalculator"),
};

export default function PublicHeader({ transparent = false }) {
  const { d, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);
  const solid = !transparent || scrolled || open;
  const blogPath = lang === "fr" ? "/blog" : `/${lang}/blog`;
  const toolsLabel = TOOLS_LABEL[lang] || TOOLS_LABEL.en;
  const tools = [
    { id: "eggcalc", to: eggcalcPath(lang), label: eggcalcData(lang).h1 },
    { id: "henprofit", to: henprofitPath(lang), label: henprofitData(lang).h1 },
    { id: "hatch", to: hatchPath(lang), label: hatchData(lang).h1 },
    { id: "feed", to: feedPath(lang), label: feedData(lang).h1 },
    { id: "coop", to: coopPath(lang), label: coopData(lang).h1 },
  ].filter((tl) => tl.to && tl.label);
  const links = [
    { to: blogPath, label: d.nav.blog, id: "blog" },
    { to: "/demo", label: d.nav.demo || DEMO_LABEL[lang] || "Demo", id: "demo" },
    { to: forumPath(lang), label: forumT(lang, "navForum"), id: "forum" },
    { to: pagePath("pricing", lang), label: d.nav.pricing, id: "pricing" },
    { to: dirPath(lang), label: dl(lang, "navDirectory"), id: "directory" },
  ];

  return (
    <header className={`${transparent ? "fixed" : "sticky"} top-0 left-0 right-0 z-50 transition-colors duration-300 ${solid ? "bg-[#FAF7F2]/90 backdrop-blur-xl border-b border-[#E5DEC4]" : "bg-transparent border-b border-transparent"}`} data-testid="public-header">
      <div className="max-w-6xl mx-auto px-5 h-24 flex items-center justify-between gap-3">
        <div className="flex items-center gap-6 min-w-0">
        <Link to="/" className="flex items-center shrink-0" data-testid="public-logo">
          <Logo className="h-20 w-auto" />
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-[#3A5644]">
          {links.map((l) => (
            <Link key={l.id} to={l.to} onMouseEnter={() => PREFETCH[l.id] && PREFETCH[l.id]()} className="hover:text-[#D96C4A] transition-colors" data-testid={`ph-${l.id}`}>{l.label}</Link>
          ))}
          <div className="relative group">
            <button onMouseEnter={PREFETCH.tools} className="flex items-center gap-1 hover:text-[#D96C4A] transition-colors" data-testid="ph-tools">{toolsLabel} <ChevronDown className="h-3.5 w-3.5" /></button>
            <div className="absolute left-0 top-full pt-3 hidden group-hover:block">
              <div className="w-80 rounded-2xl border border-[#E5DEC4] bg-[#FAF7F2] shadow-xl p-2" data-testid="ph-tools-menu">
                {tools.map((tl) => (
                  <Link key={tl.id} to={tl.to} className="block px-3 py-2 rounded-xl text-sm text-[#2C4234] hover:bg-[#4E6E58]/10 hover:text-[#D96C4A] transition-colors" data-testid={`ph-tool-${tl.id}`}>{tl.label}</Link>
                ))}
              </div>
            </div>
          </div>
        </nav>
        </div>

        <div className="flex items-center gap-2 shrink-0 md:mr-28">
          <LanguageSwitcher />
          <Link to="/login" rel="nofollow" className="hidden md:block"><Button className="rounded-2xl gap-2 h-11 px-5 font-semibold bg-[#D96C4A] hover:bg-[#BF583A] hover:scale-[1.03] transition-transform shadow-lg shadow-[#D96C4A]/30" data-testid="ph-login"><span>{d.nav.login}</span> <ArrowRight className="h-4 w-4" /></Button></Link>
          <button className="md:hidden p-2 text-[#2C4234]" onClick={() => setOpen((v) => !v)} data-testid="ph-mobile-toggle" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-[#E5DEC4] bg-[#FAF7F2] px-5 py-4 flex flex-col gap-3 text-sm font-medium text-[#4E6E58]" data-testid="ph-mobile-menu">
          {links.map((l) => (
            <Link key={l.id} to={l.to} onClick={() => setOpen(false)} className="hover:text-[#D96C4A]" data-testid={`ph-m-${l.id}`}>{l.label}</Link>
          ))}
          <div className="pt-1">
            <p className="text-xs uppercase tracking-wide text-[#4E6E58]/60 mb-1">{toolsLabel}</p>
            {tools.map((tl) => (
              <Link key={tl.id} to={tl.to} onClick={() => setOpen(false)} className="block py-1 pl-2 text-[#2C4234] hover:text-[#D96C4A]" data-testid={`ph-m-tool-${tl.id}`}>{tl.label}</Link>
            ))}
          </div>
          <Link to="/login" rel="nofollow" onClick={() => setOpen(false)} data-testid="ph-m-login">
            <Button className="w-full rounded-2xl gap-2 h-11 font-semibold bg-[#D96C4A] hover:bg-[#BF583A] shadow-lg shadow-[#D96C4A]/30">{d.nav.login} <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </nav>
      )}
    </header>
  );
}
